import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ComponentProps } from 'react';
import type { ChevronProps } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';

import { buttonVariants } from '~/components/ui/button';
import { cn } from '~/utils/cn';

export type CalendarProps = ComponentProps<typeof DayPicker>;

function CalendarChevron({ orientation }: ChevronProps) {
  if (orientation === 'left') return <ChevronLeft className="size-4" />;
  return <ChevronRight className="size-4" />;
}

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'relative flex flex-col gap-4 sm:flex-row',
        month: 'flex flex-col gap-4',
        month_caption: 'flex items-center justify-center pt-1',
        caption_label: 'text-sm font-medium',
        // Nav is a sibling of Month inside Months; absolute-position it over the
        // caption row so the buttons don't escape to an unrelated positioned ancestor.
        nav: 'absolute inset-x-1 top-1 flex items-center justify-between',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-muted-foreground w-9 rounded-md text-center text-[0.8rem] font-normal',
        week: 'mt-2 flex w-full',
        day: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-9 p-0 font-normal aria-selected:opacity-100',
        ),
        selected:
          'rounded-md bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        today: 'rounded-md bg-accent text-accent-foreground',
        outside: 'opacity-50 text-muted-foreground',
        disabled: 'opacity-50 text-muted-foreground',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{ Chevron: CalendarChevron }}
      {...props}
    />
  );
}
